import { css } from "@emotion/css";
import type { DataTableColumn, EmptyStateOnboardingCallout, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, SearchInput, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDInstanceSummaryResource, EnvironmentV2Resource, GetArgoCDInstanceSummariesResponse, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { AddArgoCDInstanceDialog } from "~/areas/infrastructure/components/ArgoCDInstances/AddArgoCDInstanceDialog";
import { ArgoCDGatewayHealthStatusIcon } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthStatusIcon";
import { EmptyStateArgoCDIllustration } from "~/areas/infrastructure/components/ArgoCDInstances/components/EmptyStateArgoCDIllustration";
import { useArgoCDIntegration } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDIntegration";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { EnvironmentsChipList } from "./components/EnvironmentsChipList";
interface ArgoCdInstancesPageProps {
    spaceId: string;
    loaderData: ArgoCDInstancesPageLoaderLoaderData;
    queryParams: ArgoCDInstancesPageLoaderQueryParams;
    setQueryParams: QueryParamValuesSetter<ArgoCDInstancesPageLoaderQueryParams>;
}
const defaultQueryParams: ArgoCDInstancesPageLoaderQueryParams = {
    environmentIds: [],
    instanceName: "",
};
export interface ArgoCDInstancesPageLoaderQueryParams {
    environmentIds: string[];
    instanceName: string;
}
export const argoCDInstancesPageTitle = "Argo CD Instances";
interface ArgoCDInstancesPageLoaderLoaderData {
    summariesResponse: GetArgoCDInstanceSummariesResponse;
    environments: EnvironmentV2Resource[];
}
export async function argoCDInstancesPageLoader(repository: Repository, queryParams: ArgoCDInstancesPageLoaderQueryParams, ephemeralEnvironmentsAreEnabled: boolean): Promise<ArgoCDInstancesPageLoaderLoaderData> {
    const response = await repository.ArgoCDInstances.list({ partialName: queryParams.instanceName, environmentIds: queryParams.environmentIds });
    const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
    return { summariesResponse: response, environments };
}
const styles = {
    centeredItem: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
    }),
    nameLink: css({
        cursor: "pointer",
    }),
};
export function ArgoCDInstancesPage({ spaceId, loaderData, queryParams, setQueryParams }: ArgoCdInstancesPageProps) {
    const hasNoEnvironments = loaderData.environments.length === 0;
    const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
    const updateQueryParams = useCallback((updated: Partial<ArgoCDInstancesPageLoaderQueryParams>) => setQueryParams((prev) => ({ ...prev, ...updated })), [setQueryParams]);
    const [nameSearch, setNameSearch] = useDebouncedExternalState(queryParams.instanceName, (val) => updateQueryParams({ instanceName: val }), 200);
    const { result: summariesResponse, refetch } = useQuery((repository) => repository.ArgoCDInstances.list({ partialName: queryParams.instanceName, environmentIds: queryParams.environmentIds }), [queryParams], "List Argo CD instances", {
        initialResult: loaderData.summariesResponse,
    });
    const argoCDIntegrationEnabled = useArgoCDIntegration();
    const { navigate } = useSpaceAwareNavigation();
    const hasAnyInstances = summariesResponse.TotalCount > 0;
    const showInstanceDialogAction: PrimaryPageAction = {
        type: "button",
        label: "Add Argo CD Instance",
        onClick: () => setShowAddDialog(true),
        hasPermissions: isAllowed({
            permission: Permission.MachineCreate,
            environment: "*",
            tenant: "*",
        }),
        disabled: hasNoEnvironments,
        extraContext: hasNoEnvironments ? "Please create an environment before creating an Argo CD instance" : undefined,
    };
    const noEnvironmentOnboardingCallout: EmptyStateOnboardingCallout | undefined = hasNoEnvironments
        ? {
            title: "Environments required",
            description: "Before you can create an Argo CD instance, you will need to create at least one environment",
            action: {
                label: "Go to Environments",
                buttonType: "secondary",
                type: "navigate",
                path: links.environmentsPage.generateUrl({ spaceId }),
            },
        }
        : undefined;
    const navigateToInstanceSettingsPage = useCallback((summaryResource: ArgoCDInstanceSummaryResource) => {
        const link = links.argoCDInstanceSettingsPage.generateUrl({
            spaceId: summaryResource.SpaceId,
            instanceId: summaryResource.Id,
        });
        navigate(link.resolveWithSpaceId(summaryResource.SpaceId));
    }, [navigate]);
    const columns: DataTableColumn<ArgoCDInstanceSummaryResource>[] = useMemo(() => [
        {
            columnSize: "large",
            title: "Name",
            render: (instanceSummary) => (<a className={styles.nameLink} onClick={() => navigateToInstanceSettingsPage(instanceSummary)}>
                        {instanceSummary.Name}
                    </a>),
        },
        {
            columnSize: "x-small",
            title: "Health",
            render: (instanceSummary) => (<div className={styles.centeredItem}>
                        <ArgoCDGatewayHealthStatusIcon healthStatus={instanceSummary.HealthStatus} iconSize={"medium"}/>
                    </div>),
        },
        {
            columnSize: "x-small",
            title: "Applications",
            render: (instanceSummary) => <div className={styles.centeredItem}>{instanceSummary.ApplicationCount}</div>,
        },
        {
            columnSize: "x-large",
            title: "Environments",
            render: (instanceSummary) => <EnvironmentsChipList environments={loaderData.environments} environmentIds={instanceSummary.EnvironmentIds}/>,
        },
    ], [loaderData.environments, navigateToInstanceSettingsPage]);
    const isFiltering = !isEqual(queryParams, defaultQueryParams);
    const filters = useMemo(() => ({
        inputs: [<SearchInput autoFocus={true} value={nameSearch} onChange={setNameSearch} accessibleName={"Search Argo CD instances by name"} placeholder={"Search by instance"}/>],
        advancedFilters: {
            content: (<AdvancedFilters>
                        <EnvironmentMultiSelect accessibleName="environmentFilter" key="filterEnvironment" items={loaderData.environments} value={queryParams.environmentIds} onChange={(x) => updateQueryParams({ environmentIds: x })}/>
                    </AdvancedFilters>),
            hasUserSelectedValues: isFiltering,
            onResetFilter: () => setQueryParams(defaultQueryParams),
        },
    }), [isFiltering, loaderData.environments, nameSearch, queryParams.environmentIds, setNameSearch, setQueryParams, updateQueryParams]);
    const handleDialogOnClose = (wasGatewayInstalled: boolean) => {
        setShowAddDialog(false);
        //if we installed a gateway, reload the gateway list!
        if (wasGatewayInstalled) {
            refetch();
        }
    };
    if (!argoCDIntegrationEnabled) {
        return <RedirectAs404 />;
    }
    return (<>
            {!hasAnyInstances && (<PortalEmptyStateOnboarding title={"Connect your Argo CD instances"} description={"Add your first Argo CD instance to combine GitOps with Octopus Deploy's powerful deployment capabilities. Start by adding an instance and installing the gateway."} primaryAction={showInstanceDialogAction} callout={noEnvironmentOnboardingCallout} image={<EmptyStateArgoCDIllustration />}/>)}
            {hasAnyInstances && (<PageContent header={{ title: argoCDInstancesPageTitle, primaryAction: showInstanceDialogAction }} filters={filters}>
                    {summariesResponse.Resources.length === 0 && <EmptyStateNoResults />}
                    {summariesResponse.Resources.length > 0 && (<BorderedContent>
                            <SimpleDataTable columns={columns} data={summariesResponse.Resources} getRowKey={(instanceSummary) => instanceSummary.Id}/>
                        </BorderedContent>)}
                </PageContent>)}
            <AddArgoCDInstanceDialog open={showAddDialog} onClose={handleDialogOnClose} spaceId={spaceId}/>
        </>);
}
